import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Devise, EtapeAffaire } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
import type { CorpsOffre, OffrePresentee } from './offres.js';
import type { CorpsReservation, ReservationPresentee } from './reservations.js';

export interface MotifPerte {
  code: 'prix' | 'deja_vendue' | 'sans_suite' | 'autre';
  texte?: string;
}
export interface AffairePresentee {
  id: string;
  numero: string;
  titre: string;
  contactId: string;
  contactNom: string;
  contactEfface: boolean;
  lignes: { oeuvreId: string; exemplaireId: string | null; montantCentimes?: number }[];
  devise: Devise;
  montantCentimes?: number;
  etape: EtapeAffaire;
  probabilite: number;
  probabiliteManuelle: boolean;
  clotureEspereeLe: string | null;
  responsableId: string | null;
  responsableNom: string | null;
  vignette: string | null;
  source: string;
  liens: {
    demandeId?: string | null;
    offreIds?: string[];
    reservationIds?: string[];
    factureIds?: string[];
  };
  motifPerte: MotifPerte | null;
  historique: {
    de: EtapeAffaire | null;
    vers: EtapeAffaire;
    le: string;
    par: string | null;
    automatique: boolean;
  }[];
  creeeLe: string;
  modifieeLe: string;
  closeLe: string | null;
  version: number;
  propositionPerte: boolean;
  enRetard: boolean;
}
export interface CorpsAffaire {
  contactId: string;
  titre?: string;
  lignes: { oeuvreId: string; exemplaireId?: string | null; montantCentimes: number }[];
  devise?: Devise;
  probabilite?: number;
  clotureEspereeLe?: string | null;
  responsableId?: string | null;
}
export interface FiltresAffaires {
  responsableId?: string;
  artisteId?: string;
  clotureDu?: string;
  clotureAu?: string;
  montantMin?: number;
  contactId?: string;
  oeuvreId?: string;
}
export interface ListeAffaires {
  affaires: AffairePresentee[];
  colonnes: Record<EtapeAffaire, { nombre: number; totalPondereCentimes?: number }>;
}
interface ChiffresPrevision {
  realiseCentimes: number;
  pondereCentimes: number;
  sansDateCentimes: number;
}
export interface PrevisionPipeline extends ChiffresPrevision {
  trimestre: { debut: string; fin: string; annee: number; numero: number };
  comparaison: ChiffresPrevision | null;
  devise: Devise;
}
export interface ReglagesPipeline {
  probabilites: Record<EtapeAffaire, number>;
  version: number;
}
export interface PlaceAttente {
  id: string;
  oeuvreId: string;
  exemplaireId: string | null;
  contactId: string;
  contactNom: string;
  note: string;
  rang: number;
  etat: 'active' | 'a_rappeler' | 'close';
  closeLe: string | null;
  motifCloture: 'vendue' | 'retiree' | null;
  ajouteeLe: string;
  ajouteePar: string | null;
  contactEfface: boolean;
  version: number;
}
export interface FiltresAttente {
  oeuvreId?: string;
  exemplaireId?: string;
  artisteId?: string;
  etat?: PlaceAttente['etat'];
}
const base = (g: string) => `/g/${encodeURIComponent(g)}`;
const affaire = (g: string, id: string) => `${base(g)}/affaires/${encodeURIComponent(id)}`;
function query(filtres: object) {
  const params = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres))
    if (valeur !== undefined && valeur !== '') params.set(cle, String(valeur));
  return params.toString();
}
export const pipelineApi = {
  lister: (g: string, filtres: FiltresAffaires = {}, appel?: AppelApi) =>
    api.get<ListeAffaires>(`${base(g)}/affaires?${query(filtres)}`, { galerie: g, ...appel }),
  lire: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ affaire: AffairePresentee }>(affaire(g, id), { galerie: g, ...appel }),
  creer: (g: string, corps: CorpsAffaire, appel?: AppelApi) =>
    api.post<{ affaire: AffairePresentee }>(`${base(g)}/affaires`, corps, { galerie: g, ...appel }),
  modifier: (
    g: string,
    id: string,
    corps: Partial<Omit<CorpsAffaire, 'contactId' | 'devise'>> & { version: number },
    appel?: AppelApi,
  ) => api.patch<{ affaire: AffairePresentee }>(affaire(g, id), corps, { galerie: g, ...appel }),
  deplacer: (
    g: string,
    id: string,
    corps: { vers: EtapeAffaire; version: number; motifPerte?: MotifPerte },
    appel?: AppelApi,
  ) =>
    api.post<{ affaire: AffairePresentee }>(`${affaire(g, id)}/etape`, corps, {
      galerie: g,
      ...appel,
    }),
  rouvrir: (
    g: string,
    id: string,
    corps: { vers: 'interet' | 'offre' | 'reservation'; version: number },
    appel?: AppelApi,
  ) =>
    api.post<{ affaire: AffairePresentee }>(`${affaire(g, id)}/rouvrir`, corps, {
      galerie: g,
      ...appel,
    }),
  depuis: (g: string, source: 'demande' | 'offre', id: string, appel?: AppelApi) =>
    api.post<{ affaire: AffairePresentee }>(
      `${base(g)}/affaires/depuis-${source}/${encodeURIComponent(id)}`,
      {},
      { galerie: g, ...appel },
    ),
  creerOffre: (g: string, id: string, corps: CorpsOffre, appel?: AppelApi) =>
    api.post<{ affaire: AffairePresentee; offre: OffrePresentee }>(
      `${affaire(g, id)}/offres`,
      corps,
      { galerie: g, ...appel },
    ),
  reserver: (g: string, id: string, corps: CorpsReservation, appel?: AppelApi) =>
    api.post<{ affaire: AffairePresentee; reservation: ReservationPresentee }>(
      `${affaire(g, id)}/reservations`,
      corps,
      { galerie: g, ...appel },
    ),
  prevision: (g: string, trimestre: 'en_cours' | 'suivant' = 'en_cours', appel?: AppelApi) =>
    api.get<PrevisionPipeline>(`${base(g)}/pipeline/prevision?trimestre=${trimestre}`, {
      galerie: g,
      ...appel,
    }),
  reglages: (g: string, appel?: AppelApi) =>
    api.get<ReglagesPipeline>(`${base(g)}/pipeline/reglages`, { galerie: g, ...appel }),
  regler: (g: string, corps: ReglagesPipeline, appel?: AppelApi) =>
    api.put<ReglagesPipeline>(`${base(g)}/pipeline/reglages`, corps, { galerie: g, ...appel }),
  attente: (g: string, filtres: FiltresAttente, appel?: AppelApi) =>
    api.get<{ places: PlaceAttente[] }>(`${base(g)}/listes-attente?${query(filtres)}`, {
      galerie: g,
      ...appel,
    }),
  ajouterAttente: (
    g: string,
    corps: { oeuvreId: string; exemplaireId?: string | null; contactId: string; note?: string },
    appel?: AppelApi,
  ) =>
    api.post<{ place: PlaceAttente }>(`${base(g)}/listes-attente`, corps, { galerie: g, ...appel }),
  modifierAttente: (
    g: string,
    id: string,
    corps: { note: string; version: number },
    appel?: AppelApi,
  ) =>
    api.patch<{ place: PlaceAttente }>(
      `${base(g)}/listes-attente/${encodeURIComponent(id)}`,
      corps,
      { galerie: g, ...appel },
    ),
  ordonnerAttente: (
    g: string,
    corps: { oeuvreId: string; exemplaireId?: string | null; ids: string[] },
    appel?: AppelApi,
  ) => api.put<void>(`${base(g)}/listes-attente/ordre`, corps, { galerie: g, ...appel }),
  retirerAttente: (g: string, id: string, appel?: AppelApi) =>
    api.delete<void>(`${base(g)}/listes-attente/${encodeURIComponent(id)}`, {
      galerie: g,
      ...appel,
    }),
};
export function useAffaires(g: string, filtres: FiltresAffaires = {}, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'pipeline', 'affaires', filtres),
    queryFn: (c) => pipelineApi.lister(g, filtres, appelDeRequete(c)),
    enabled,
  });
}
export function useAffaire(g: string, id: string | null) {
  return useQuery({
    queryKey: cleMetier(g, 'pipeline', 'affaire', id),
    queryFn: (c) => pipelineApi.lire(g, id!, appelDeRequete(c)),
    enabled: id !== null,
  });
}
export function usePrevision(g: string, trimestre: 'en_cours' | 'suivant', enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'pipeline', 'prevision', trimestre),
    queryFn: (c) => pipelineApi.prevision(g, trimestre, appelDeRequete(c)),
    enabled,
    retry: false,
  });
}
export function useAttente(g: string, filtres: FiltresAttente) {
  return useQuery({
    queryKey: cleMetier(g, 'pipeline', 'attente', filtres),
    queryFn: (c) => pipelineApi.attente(g, filtres, appelDeRequete(c)),
  });
}
export function useReglagesPipeline(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'pipeline', 'reglages'),
    queryFn: (c) => pipelineApi.reglages(g, appelDeRequete(c)),
  });
}
export function useActionPipeline<T, V>(
  g: string,
  action: (corps: V, appel: AppelApi) => Promise<T>,
) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: action,
      onSettled: () => {
        for (const domaine of ['pipeline', 'offres', 'reservations', 'oeuvres'])
          void client.invalidateQueries({ queryKey: cleMetier(g, domaine) });
      },
    }),
  );
}
